import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';

// Axios instance for media endpoints (without /user/ prefix)
const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/`
);

export interface CVAnalysisResponse {
  overall_score: number;
  score_bucket: string;
  category_scores: {
    sections_completeness: number;
    skills: number;
    contact_information: number;
    photo: number;
    keywords_and_action_verbs: number;
    impact_quantification: number;
    repetition_and_pronouns: number;
    parsability: number;
    formatting: number;
    spelling_and_grammar: number;
  };
  section_presence: {
    summary: boolean;
    experience: boolean;
    education: boolean;
    skills: boolean;
    contact_information: boolean;
    nationality: boolean;
    visa_status: boolean;
    languages: boolean;
    photo: boolean;
    courses_certifications: boolean;
    achievements_awards: boolean;
  };
  strengths: Array<{
    title: string;
    description: string;
  }>;
  improvements: Array<{
    title: string;
    description: string;
  }>;
  error?: {
    message: string;
    code: number;
  };
}

export interface CVAnalyzerLimitError {
  error: {
    message: string;
    code: number;
  };
}

const analyzeCV = async (
  formData: FormData
): Promise<CVAnalysisResponse | CVAnalyzerLimitError> => {
  try {
    const response = await axios.post<{data: CVAnalysisResponse; message: string; statusCode: number} | CVAnalysisResponse>(
      'media/cv-analyser',
      formData
    );
    
    
    const analysisData = 'data' in response.data && response.data.data 
      ? response.data.data 
      : response.data;
    

    return analysisData as CVAnalysisResponse;
  } catch (error: any) {
    
    // Handle different error scenarios
    if (error.response) {
      const {status, data} = error.response;
      
      // Rate limit error (429/403)
      if (status === 429 || status === 403) {
        const rateLimitError: CVAnalyzerLimitError = {
          error: {
            message: data?.error?.message || "You've reached the CV Analyzer limit for this device. Please try again later.",
            code: 429,
          },
        };
        return rateLimitError;
      }
      
      // System error (500+)
      if (status >= 500) {
        const systemError: CVAnalyzerLimitError = {
          error: {
            message: data?.error?.message || 'Failed to analyze your CV. Please try again later.',
            code: 500,
          },
        };
        return systemError;
      }
      
      // Other API errors (400, etc.)
      const apiError: CVAnalyzerLimitError = {
        error: {
          message: data?.error?.message || 'An error occurred during CV analysis.',
          code: status,
        },
      };
      return apiError;
    }
    
    // Network error
    const networkError: CVAnalyzerLimitError = {
      error: {
        message: 'Network error. Please check your connection and try again.',
        code: 0,
      },
    };
    return networkError;
  }
};

const cvAnalyzerService = {
  analyzeCV,
};

export default cvAnalyzerService;
